import React, { FunctionComponent, RefObject, useCallback, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CartInput from "@Components/atoms/cart/CartInput";
import useCartContext from "@Hooks/useCartContext";
import { DeliveryAddress, MondialRelayLocationContent, ShippingOptions } from "@Types/addresses";
import UserContent from "@Types/user";
import { formatDates } from "@Utils/cart/deliveryMethods";
import { emailPattern, userNamePattern } from "@Utils/checkFormats";
import expressDeliveryIcon from "./images/express.png";
import { ReactComponent as LocationIcon } from "./images/location.svg";
import normalDeliveryIcon from "./images/normal.png";
import "./style.scss";

interface Props extends ContainerProps {
  deliveryAddress: DeliveryAddress;
  onChangeDeliveryAddress: (deliveryAddress: DeliveryAddress) => void;
  user: UserContent;
}

interface ContainerProps {
  mondialRelayLocation: MondialRelayLocationContent;
  mondialRelayShippingOptions: ShippingOptions;
  onChangeLocation: () => void;
}

export const CartMondialRelayOverview: FunctionComponent<Props> = ({
  deliveryAddress,
  mondialRelayLocation,
  mondialRelayShippingOptions,
  onChangeLocation,
  onChangeDeliveryAddress,
  user,
}) => {
  const intl = useIntl();
  const emailDeliveryRef = useRef<HTMLInputElement>(null);
  const firstNameDeliveryRef = useRef<HTMLInputElement>(null);
  const lastNameDeliveryRef = useRef<HTMLInputElement>(null);
  const phoneDeliveryRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(() => {
    const updatedAddress = {
      ...deliveryAddress,
      email: getValueFromRef(emailDeliveryRef),
      firstName: getValueFromRef(firstNameDeliveryRef),
      lastName: getValueFromRef(lastNameDeliveryRef),
      phone: getValueFromRef(phoneDeliveryRef),
    };
    onChangeDeliveryAddress(updatedAddress);
  }, [onChangeDeliveryAddress, deliveryAddress]);

  const getValueFromRef = (ref: RefObject<HTMLInputElement>): string => {
    return ref && ref.current ? ref.current.value : "";
  };

  const getSelectedMode = () => {
    const isExpressSelected = mondialRelayShippingOptions.express.selected;
    return isExpressSelected
      ? mondialRelayShippingOptions.express
      : mondialRelayShippingOptions.normal;
  };

  const formatDeliveryDates = () => {
    const selectedMode = getSelectedMode();
    if (selectedMode.deliveryDates) {
      return formatDates(selectedMode.deliveryDates[0], selectedMode.deliveryDates[1], intl.locale);
    }
  };

  const formatDeliveryName = () => {
    return mondialRelayShippingOptions.express.selected
      ? "cheerz_CartMondialRelayOverview_delivery_express"
      : "cheerz_CartMondialRelayOverview_delivery_normal";
  };

  const formatDeliveryPrice = () => {
    const selectedMode = getSelectedMode();
    return selectedMode.price ? selectedMode.price.formattedPrice : "";
  };

  const getDeliveryIcon = () => {
    return mondialRelayShippingOptions.express.selected ? expressDeliveryIcon : normalDeliveryIcon;
  };

  return (
    <div className="CartMondialRelayOverview">
      <div className="CartMondialRelayOverview-overview">
        <div className="CartMondialRelayOverview-overview-location">
          <div className="CartMondialRelayOverview-overview-location-icon">
            <LocationIcon />
          </div>
          <div className="CartMondialRelayOverview-overview-location-name">
            {mondialRelayLocation.company}
          </div>
          <div className="CartMondialRelayOverview-overview-location-address">
            {mondialRelayLocation.street}
          </div>
          <div className="CartMondialRelayOverview-overview-location-city">
            <span>{mondialRelayLocation.postcode}</span>
            <span>{mondialRelayLocation.city}</span>
          </div>
        </div>
        <div className="CartMondialRelayOverview-overview-delivery">
          <img
            alt=""
            className="CartMondialRelayOverview-overview-delivery-icon"
            src={getDeliveryIcon()}
          />
          <div className="CartMondialRelayOverview-overview-delivery-date">
            {formatDeliveryDates()}
          </div>
          <div className="CartMondialRelayOverview-overview-delivery-name">
            <FormattedMessage id={formatDeliveryName()} />
          </div>
          <div className="CartMondialRelayOverview-overview-delivery-price">
            {formatDeliveryPrice()}
          </div>
        </div>
      </div>
      <div className="CartMondialRelayOverview-changeLocation" onClick={() => onChangeLocation()}>
        <FormattedMessage id={"cheerz_CartMondialRelayOverview_change_location"} />
      </div>
      <div className="CartMondialRelayOverview-instructions">
        <FormattedMessage id={"cheerz_CartMondialRelayOverview_instructions"} />
      </div>
      <div className="CartMondialRelayOverview-warning">
        <FormattedMessage id={"cheerz_CartMondialRelayOverview_warning"} />
      </div>
      <div className="CartMondialRelayOverview-form">
        <div className="CartMondialRelayOverview-form-line">
          <CartInput
            autocomplete="shipping given-name"
            name="firstName"
            onChange={handleChange}
            pattern={userNamePattern}
            placeholder={intl.formatMessage({
              id: "cheerz_CartMondialRelayOverview_form_firstName",
            })}
            ref={firstNameDeliveryRef}
            required={true}
            value={deliveryAddress.firstName}
          />
          <CartInput
            autocomplete="shipping family-name"
            name="lastName"
            onChange={handleChange}
            pattern={userNamePattern}
            placeholder={intl.formatMessage({
              id: "cheerz_CartMondialRelayOverview_form_lastName",
            })}
            ref={lastNameDeliveryRef}
            required={true}
            value={deliveryAddress.lastName}
          />
          <CartInput
            autocomplete="shipping tel"
            name="phone"
            onChange={handleChange}
            placeholder={intl.formatMessage({ id: "cheerz_CartMondialRelayOverview_form_phone" })}
            ref={phoneDeliveryRef}
            type="tel"
            value={deliveryAddress.phone}
          />
        </div>
        {user.isGuest && (
          <div className="CartMondialRelayOverview-form-line">
            <CartInput
              autocomplete="shipping email"
              name="email"
              onChange={handleChange}
              pattern={emailPattern}
              placeholder={intl.formatMessage({ id: "cheerz_CartMondialRelayOverview_form_email" })}
              ref={emailDeliveryRef}
              required={true}
              type="email"
              value={deliveryAddress.email}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const CartMondialRelayOverviewContainer: FunctionComponent<ContainerProps> = ({
  mondialRelayLocation,
  mondialRelayShippingOptions,
  onChangeLocation,
}) => {
  const { addresses, dispatch, user } = useCartContext();

  const handleChangeDeliveryAddress = (deliveryAddress: DeliveryAddress) => {
    dispatch({
      address: deliveryAddress,
      type: "setDeliveryAddress",
    });
  };

  return (
    <CartMondialRelayOverview
      deliveryAddress={addresses.delivery}
      mondialRelayLocation={mondialRelayLocation}
      mondialRelayShippingOptions={mondialRelayShippingOptions}
      onChangeDeliveryAddress={handleChangeDeliveryAddress}
      onChangeLocation={onChangeLocation}
      user={user}
    />
  );
};

export default CartMondialRelayOverviewContainer;
